import { useState, useEffect } from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { extractYouTubeVideoId } from "@/lib/youtube";
import { SponsoredVideoModal } from "@/components/SponsoredVideoModal";

interface SponsoredAd {
  id: string;
  title: string;
  title_above: string | null;
  description: string | null;
  cta_enabled: boolean;
  cta_text: string | null;
  cta_url: string | null;
  thumbnail_url: string | null;
  youtube_video_id: string;
  sponsor_text: string | null;
  is_public: boolean;
}

export const VenueStoryPreview = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeAd, setActiveAd] = useState<SponsoredAd | null>(null);
  const [loading, setLoading] = useState(true);
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  // Read ?preview_ad=<id> from the URL (admin-only preview of a specific ad)
  const previewAdId = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("preview_ad")
    : null;
  const isPreviewMode = !!previewAdId && isAdmin;

  useEffect(() => {
    if (previewAdId && adminLoading) {
      setLoading(true);
      return;
    }

    fetchActiveAd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewAdId, isAdmin, adminLoading]);

  useEffect(() => {
    if (isPreviewMode && activeAd) {
      document.getElementById("sponsored-ad")?.scrollIntoView({ block: "start" });
    }
  }, [isPreviewMode, activeAd]);

  const fetchActiveAd = async () => {
    setLoading(true);
    try {
      // Admin previewing a specific ad — bypass active/date/public filters
      if (previewAdId) {
        if (!isAdmin) {
          setActiveAd(null);
          return;
        }

        const { data, error } = await supabase
          .from('sponsored_ads')
          .select('*')
          .eq('id', previewAdId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching sponsored ad (preview):', error);
        } else {
          setActiveAd(data);
        }
        return;
      }

      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('sponsored_ads')
        .select('*')
        .eq('is_active', true)
        .lte('display_from', today)
        .gte('display_to', today)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching sponsored ad:', error);
      } else {
        setActiveAd(data);
      }
    } catch (err) {
      console.error('Error fetching sponsored ad:', err);
    } finally {
      setLoading(false);
    }
  };

  // Don't render anything if loading or no active ad
  if (loading || !activeAd) {
    return null;
  }

  // Only show to non-admins if the ad is published (is_public)
  // (Preview mode is always admin-gated above, so this still applies for normal viewers.)
  if (!isAdmin && !activeAd.is_public) {
    return null;
  }

  const videoId = extractYouTubeVideoId(activeAd.youtube_video_id);

  // Use custom thumbnail if available, otherwise fall back to YouTube thumbnail
  const thumbnailUrl = activeAd.thumbnail_url ||
    (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null);

  // If we can't resolve a playable video, don't render the module (avoids broken embeds)
  if (!videoId || !thumbnailUrl) {
    return null;
  }
  
  return (
    <>
      <section id="sponsored-ad" className="bg-light-fuschia/40 border-t border-border/40 py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isPreviewMode && (
            <div className="mb-4 flex justify-center lg:justify-start">
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                👁️ Preview mode — only admins can see this
              </span>
            </div>
          )}
          {/* Grid layout: stacked on mobile, side-by-side on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-8 lg:gap-12 items-center">
            {/* Clickable Video Thumbnail */}
            <button
              onClick={() => setIsOpen(true)}
              className="w-full max-w-[360px] mx-auto lg:mx-0 group cursor-pointer relative"
            >
              <AspectRatio ratio={16 / 9} className="rounded-xl overflow-hidden shadow-soft">
                <img
                  src={thumbnailUrl}
                  alt={activeAd.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="w-7 h-7 text-primary ml-0.5" fill="currentColor" />
                  </div>
                </div>
              </AspectRatio>
            </button>

            {/* Text Content — constrained width for readability */}
            <div className="text-center lg:text-left max-w-xl mx-auto lg:mx-0 space-y-3">
              {/* Small Label */}
              {activeAd.title_above && (
                <p className="text-xs font-semibold text-primary uppercase tracking-wider">
                  {activeAd.title_above}
                </p>
              )}

              {/* Title */}
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
                {activeAd.title}
              </h2>

              {/* Description */}
              {activeAd.description && (
                <p className="text-base text-muted-foreground leading-relaxed">
                  {activeAd.description}
                </p>
              )}

              {/* CTA Button */}
              {activeAd.cta_enabled && activeAd.cta_text && activeAd.cta_url && (
                <div className="pt-2">
                  <Button
                    variant="default"
                    size="default"
                    className="hover:bg-[#DF6283] hover:text-white"
                    asChild
                  >
                    <a
                      href={activeAd.cta_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => import("@/lib/adTracking").then(m => m.trackAdClick("sponsored", activeAd.id))}
                    >
                      {activeAd.cta_text}
                    </a>
                  </Button>
                </div>
              )}

              {/* Sponsor Label */}
              {activeAd.sponsor_text && (
                <p className="text-xs text-muted-foreground italic pt-1">
                  {activeAd.sponsor_text}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <SponsoredVideoModal
        open={isOpen}
        onOpenChange={setIsOpen}
        videoId={videoId}
        title={activeAd.title}
        adId={activeAd.id}
      />
    </>
  );
};
